import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import BlogNavbar from './BlogNavbar';
import BlogFooter from './BlogFooter';
import BlogCard from './BlogCard';
import '../Styles/Blog.css';
import '../Styles/BlogCover.css';
import { BotanicalBranch, BotanicalLeaf, BotanicalSpiral } from './BotanicalSVG';
import SEO from '../components/SEO';
import { PP_EDIT_DEFAULTS } from '../config/ppEditDefaults';
import NewsletterSubscribe from '../components/NewsletterSubscribe';
import ppEditText from '../assets/images/pp-edit-text.png';
import ppEditFlower from '../assets/images/pp-edit-flower.png';
import poojaPortrait from '../assets/images/KnowMoreAboutMe.jpeg';

const SkeletonCard = () => (
  <div className="blog-card blog-skel-card" aria-hidden="true">
    <div className="blog-skel-img" />
    <div className="blog-card-content">
      <div className="blog-skel-line" style={{ width: '40%', height: 12, marginBottom: 14 }} />
      <div className="blog-skel-line" style={{ width: '85%', height: 22, marginBottom: 10 }} />
      <div className="blog-skel-line" style={{ width: '70%', height: 22, marginBottom: 16 }} />
      <div className="blog-skel-line" style={{ width: '95%', height: 14, marginBottom: 8 }} />
      <div className="blog-skel-line" style={{ width: '80%', height: 14 }} />
    </div>
  </div>
);

const BlogHome = () => {
  const [posts, setPosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag]     = useState('ALL');
  const [pageConfig, setPageConfig] = useState(PP_EDIT_DEFAULTS);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const snap = await getDocs(collection(db, 'blogs'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.draft !== true);
        setPosts(all.sort((a, b) => {
          const aMs = a.createdAt?.seconds ?? a.createdAt?.toMillis?.() ?? 0;
          const bMs = b.createdAt?.seconds ?? b.createdAt?.toMillis?.() ?? 0;
          return bMs - aMs;
        }));
      } catch (err) {
        console.error('BlogHome fetch error:', err);
        setError("Couldn't load articles — please refresh.");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    getDoc(doc(db, 'page_config', 'pp_edit'))
      .then(snap => snap.exists() && setPageConfig({
        ...PP_EDIT_DEFAULTS,
        ...snap.data(),
        background: '#FFF0F5',
        paper: '#FFF0F5',
        fadedAccent: '#FFE4E1',
      }))
      .catch(() => {});
  }, []);

  /* All unique tags derived from posts */
  const allTags = useMemo(() => {
    const tags = new Set();
    posts.forEach(p => p.tags?.forEach(t => tags.add(t)));
    return ['ALL', ...Array.from(tags)];
  }, [posts]);

  /* Filter by active tag + search text */
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return posts.filter(p => {
      const tagMatch  = activeTag === 'ALL' || p.tags?.includes(activeTag);
      const textMatch = !q ||
        p.title?.toLowerCase().includes(q) ||
        p.excerpt?.toLowerCase().includes(q) ||
        p.tags?.some(t => t.toLowerCase().includes(q));
      return tagMatch && textMatch;
    });
  }, [posts, activeTag, searchQuery]);

  const featured = filtered.filter(p => p.featured);
  const rest     = filtered.filter(p => !p.featured);

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The PP Edit",
    description: "Expert articles on contract law, freelance business strategy, commercial agreements and professional growth.",
    url: "https://pp-studio-website.web.app/blog",
    author: { "@type": "Person", name: "Pooja Biswal" },
    publisher: {
      "@type": "Organization",
      name: "The PP Studio",
      url: "https://pp-studio-website.web.app",
      logo: { "@type": "ImageObject", url: "https://pp-studio-website.web.app/pp-favicon-192.png" },
    },
  };

  return (
    <div className={`blog-container ppe-${pageConfig.photoStyle} ppe-card-${pageConfig.cardStyle}${pageConfig.motion ? '' : ' ppe-no-motion'}`} style={{
      '--ppe-bg': pageConfig.background, '--ppe-paper': pageConfig.paper, '--ppe-ink': pageConfig.ink,
      '--ppe-accent': pageConfig.accent, '--ppe-faded': pageConfig.fadedAccent,
      '--ppe-texture-opacity': Number(pageConfig.texture) / 100, '--ppe-columns': pageConfig.columns,
    }}>
      <SEO
        title="The PP Edit — Contract Law & Freelance Business Insights"
        description="Expert articles on contract law, freelance business strategy, NDAs, service agreements and commercial contracts. Written by Pooja Biswal, LLM."
        keywords="contract law articles, freelance legal advice UK, NDA guide, service agreement tips, commercial contracts blog, contract specialist insights"
        canonical="/blog"
        schema={blogSchema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "The PP Edit", path: "/blog" }]}
      />
      <BlogNavbar />

      {/* ── HERO ── */}
      <section className="blog-hero">
        {pageConfig.heroImage && (
          <div className="blog-hero-portrait" aria-hidden="true">
            <img src={pageConfig.heroImage} alt="" />
            <span className="blog-hero-portrait-credit">— Pooja Biswal</span>
          </div>
        )}
        <div className="blog-issue-rail" aria-hidden="true">
          <span>Independent journal · London</span>
          <span>Issue No. 01 · Personal, practical, unfiltered</span>
        </div>
        <div className="blog-hero-content">
          <p className="blog-hero-kicker">{pageConfig.kicker}</p>
          <h1 className="blog-title blog-title-logo">
            <span className="pp-edit-logo-stage">
              <img src={ppEditText} alt={pageConfig.title} className="pp-edit-logo-text" />
              <img src={ppEditFlower} alt="" aria-hidden="true" className="pp-edit-logo-flower" />
            </span>
          </h1>
          <p className="blog-subtitle">{pageConfig.subtitle}</p>
          <div className="blog-hero-lines" aria-label="Editorial themes">
            <span>{pageConfig.themeLine1}</span><span>{pageConfig.themeLine2}</span><span>{pageConfig.themeLine3}</span>
          </div>

          {/* Search */}
          <div className="blog-search-wrap">
            <svg className="blog-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="blog-search"
              type="search"
              placeholder={pageConfig.searchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Search blog"
            />
            {searchQuery && (
              <button className="blog-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">✕</button>
            )}
          </div>
          <p className="blog-hero-note">{pageConfig.browseNote}</p>
          <button
            className="blog-enter-journal"
            type="button"
            onClick={() => document.getElementById('journal-start')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            <span>Enter the journal</span>
            <i aria-hidden="true">↓</i>
          </button>
        </div>
      </section>

      {!loading && !error && (
        <section id="journal-start" className="pooja-editor-card" aria-labelledby="pooja-editor-title">
          <div className="pooja-editor-photo">
            <img src={poojaPortrait} alt="Pooja, editor of The PP Edit" loading="lazy" />
          </div>
          <div className="pooja-editor-copy">
            <p>From the editor</p>
            <h2 id="pooja-editor-title">A personal edit, by Pooja.</h2>
            <blockquote>“The things worth knowing rarely arrive as headlines.”</blockquote>
            <span>This is where work, contracts, ambition and the quiet moments in between become stories worth keeping.</span>
            <Link to="/#intro">Meet Pooja <b aria-hidden="true">→</b></Link>
          </div>
        </section>
      )}

      {!loading && !error && (pageConfig.currentlyReading || pageConfig.currentlyThinking || pageConfig.currentlyGrateful) && (
        <section className="pp-edit-currently" aria-label={pageConfig.currentlyLabel || 'Right now'}>
          <div className="pp-edit-currently-inner">
            <span className="pp-edit-currently-label">{pageConfig.currentlyLabel || 'Right now'}</span>
            <div className="pp-edit-currently-items">
              {pageConfig.currentlyReading && (
                <div className="pp-edit-currently-item">
                  <span className="pp-edit-currently-tag">Reading</span>
                  <p>{pageConfig.currentlyReading}</p>
                </div>
              )}
              {pageConfig.currentlyThinking && (
                <div className="pp-edit-currently-item">
                  <span className="pp-edit-currently-tag">On my mind</span>
                  <p>{pageConfig.currentlyThinking}</p>
                </div>
              )}
              {pageConfig.currentlyGrateful && (
                <div className="pp-edit-currently-item">
                  <span className="pp-edit-currently-tag">Grateful for</span>
                  <p>{pageConfig.currentlyGrateful}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── TAG FILTERS ── */}
      {!loading && allTags.length > 1 && (
        <div className="blog-tags-filter" role="tablist" aria-label="Filter by tag">
          {allTags.map(tag => (
            <button
              key={tag}
              role="tab"
              aria-selected={activeTag === tag}
              className={`blog-tag-btn${activeTag === tag ? ' active' : ''}`}
              onClick={() => setActiveTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <p className="blog-results-status" aria-live="polite">
        {!loading && !error && (searchQuery || activeTag !== 'ALL')
          ? `${filtered.length} ${filtered.length === 1 ? 'story' : 'stories'} found`
          : ''}
      </p>

      {/* ── SKELETON LOADING ── */}
      {loading && (
        <section className="blog-section">
          <div className="blog-content-wrapper">
            <div className="blog-grid">
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </div>
          </div>
        </section>
      )}

      {/* ── ERROR ── */}
      {error && (
        <div className="blog-error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try again</button>
        </div>
      )}

      {/* ── FEATURED ── */}
      {!loading && !error && featured.length > 0 && (
        <section className="blog-section featured-section">
          <BotanicalBranch style={{ position: 'absolute', top: 20, right: 20, opacity: 0.2, zIndex: 0 }} />
          <BotanicalLeaf   style={{ position: 'absolute', bottom: 30, left: 30, opacity: 0.18, zIndex: 0 }} />
          <BotanicalSpiral style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', opacity: 0.08, zIndex: 0 }} />
          <div className="blog-content-wrapper">
            <h2 className="section-title">{pageConfig.featuredLabel}</h2>
            <div className={`blog-grid featured-grid${featured.length === 1 ? ' single-featured' : ''}`}>
              {featured.map(post => (
                <BlogCard key={post.id} post={post} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── RECENT / FILTERED ── */}
      {!loading && !error && rest.length > 0 && (
        <section className="blog-section">
          <div className="blog-content-wrapper">
            <h2 className="section-title">
              {searchQuery || activeTag !== 'ALL'
                ? `Results (${filtered.length})`
                : pageConfig.recentLabel}
            </h2>
            <div className="blog-grid">
              {rest.map(post => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── EMPTY STATE ── */}
      {!loading && !error && filtered.length === 0 && (
        <div className="blog-empty-state">
          <p>No articles found{searchQuery ? ` for "${searchQuery}"` : ''}.</p>
          <button onClick={() => { setSearchQuery(''); setActiveTag('ALL'); }}>
            Clear filters
          </button>
        </div>
      )}

      {/* ── EDITORIAL SIGN-OFF — final invitation before the footer ── */}
      {!loading && !error && (
        <section id="join-the-journal" className={`pp-edit-journal-close${pageConfig.keepsakePhoto ? ' has-photo' : ''}`} aria-label="Join The PP Edit journal">
          <div className="pp-edit-journal-close-inner">
            {pageConfig.keepsakePhoto && (
              <figure className="pp-edit-journal-close-photo">
                <img src={pageConfig.keepsakePhoto} alt="Pooja Biswal, editor of The PP Edit" loading="lazy" />
                <figcaption>
                  <span>Behind the edit</span>
                  <em>Work, life, and the moments in between.</em>
                </figcaption>
              </figure>
            )}
            <div className="pp-edit-journal-close-invite">
              <NewsletterSubscribe
                theme="edit"
                defaultLists={['pp_edit']}
                kicker="Join the journal"
                title="Get each new entry, gently delivered."
                subtitle="One honest, unfiltered read — straight to your inbox, never more than once a week."
              />
            </div>
          </div>
        </section>
      )}

      <BlogFooter />
    </div>
  );
};

export default BlogHome;
